import { PencilIcon, TrashIcon } from '@heroicons/react/20/solid';
import IconButton from '../../../IconButton';
import Container from '../../../Container';
import { getResources, queryClient } from '../../../../util/request';
import Modal from '../../../Modal';
import {
  isAuthorizedSuper,
  isAuthorizedSuperAdmin,
} from '../../../../util/helper';
import { Link, LoaderFunction } from 'react-router-dom';
import Badge from '../../../Badge';
import useOperation from '../../../../hooks/operation';
import { useQuery } from '@tanstack/react-query';

export default function Testimonials() {
  const { data } = useQuery({
    queryKey: ['testimonials'],
    queryFn: ({ signal }) => getResources({ signal, url: 'testimonials' }),
  });
  const testimonials: Testimonial[] = data?.data;
  const { id, show, handleDelete, markObjectForDeletion, closeModal } =
    useOperation();

  return (
    <Container>
      <Modal
        show={show}
        onClose={closeModal}
        onAction={() => handleDelete(`testimonials/${id}`, ['testimonials'])}
      />
      {testimonials && testimonials.length > 0 ? (
        <>
          <Container.Heading>Testimonials</Container.Heading>
          <Container.SubHeading>List of all testimonials.</Container.SubHeading>
          <div className='overflow-x-auto rounded-md'>
            <table className='min-w-full divide-y divide-gray-200 table-auto'>
              <thead className='bg-gray-100'>
                <tr>
                  <th
                    scope='col'
                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    #
                  </th>
                  <th
                    scope='col'
                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    First name
                  </th>
                  <th
                    scope='col'
                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Last name
                  </th>
                  <th
                    scope='col'
                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Company name
                  </th>
                  <th
                    scope='col'
                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Description
                  </th>
                  <th
                    scope='col'
                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Post status
                  </th>

                  {isAuthorizedSuperAdmin() && (
                    <th
                      scope='col'
                      className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                    >
                      Operation
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200 p-0'>
                {testimonials &&
                  testimonials.length > 0 &&
                  testimonials.map((testimonial, index) => (
                    <tr
                      key={testimonial.id}
                      className='hover:bg-gray-100 transition-all'
                    >
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                        {index + 1}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                        {testimonial.firstName}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                        {testimonial.lastName}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                        {testimonial.companyName}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                        {`${testimonial.description.substring(0, 30)}...`}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                        <Badge status={testimonial.postStatus}>
                          {testimonial.postStatus}
                        </Badge>
                      </td>
                      {isAuthorizedSuperAdmin() && (
                        <td className='px-6 py-4 flex gap-2'>
                          <Link
                            to={`/dashboard/testimonials/${testimonial.id}`}
                          >
                            <IconButton
                              icon={
                                <PencilIcon
                                  className='h-5 w-5 text-indigo-400'
                                  aria-hidden='true'
                                />
                              }
                            />
                          </Link>
                          {isAuthorizedSuper() && (
                            <IconButton
                              onClick={() =>
                                markObjectForDeletion(testimonial.id)
                              }
                              icon={
                                <TrashIcon
                                  className='h-5 w-5 text-red-400'
                                  aria-hidden='true'
                                />
                              }
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <Container.Heading>No data</Container.Heading>
      )}
    </Container>
  );
}

export async function loader(): Promise<LoaderFunction> {
  return await queryClient.fetchQuery({
    queryKey: ['testimonials'],
    queryFn: ({ signal }) => getResources({ signal, url: 'testimonials' }),
  });
}
